// LoginPage.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FaUser,
  FaLock,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaKey,
  FaSync,
  FaClock,
  FaCheckCircle,
  FaQuoteLeft,
  FaQuoteRight,
  FaGlobe,
  FaLanguage,
  FaShieldAlt,
  FaSignInAlt,
  FaMobileAlt,
  FaQrcode,
  FaFingerprint,
  FaGraduationCap,
  FaBook,
  FaUsers,
  FaCaretLeft,
  FaCaretRight
} from "react-icons/fa";
import "./App.css";

// API Configuration
// const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api";
const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api";

const AUTH_API = `${API_BASE_URL}/users.php`;

const LoginPage = () => {
  // State variables
  const [loginData, setLoginData] = useState({
    username: "",
    password: "",
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  
  // Forgot Password States
  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: "",
    verificationCode: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: Email, 2: Code, 3: Reset
  const [codeTimer, setCodeTimer] = useState(0);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [generatedCode, setGeneratedCode] = useState("");
  
  // Language and Quotes
  const [language, setLanguage] = useState("english");
  const [currentQuote, setCurrentQuote] = useState(0);
  
  // Floating particles
  const [particles, setParticles] = useState([]);
  
  // Password strength
  const [passwordStrength, setPasswordStrength] = useState("weak");

  // Motivational Quotes in English and Urdu
  const quotes = [
    {
      english: "Education is the most powerful weapon which you can use to change the world.",
      urdu: "تعلیم سب سے طاقتور ہتھیار ہے جسے آپ دنیا کو تبدیل کرنے کے لیے استعمال کر سکتے ہیں۔",
      author: "Nelson Mandela",
    },
    {
      english: "The roots of education are bitter, but the fruit is sweet.",
      urdu: "تعلیم کی جڑیں تلخ ہوتی ہیں، لیکن پھل میٹھا ہوتا ہے۔",
      author: "Aristotle",
    },
    {
      english: "An investment in knowledge pays the best interest.",
      urdu: "علم میں سرمایہ کاری سب سے بہترین سود دیتی ہے۔",
      author: "Benjamin Franklin",
    },
    {
      english: "Education is not preparation for life; education is life itself.",
      urdu: "تعلیم زندگی کی تیاری نہیں؛ تعلیم خود زندگی ہے۔",
      author: "John Dewey",
    },
    {
      english: "The beautiful thing about learning is that no one can take it away from you.",
      urdu: "سیکھنے کی خوبصورت بات یہ ہے کہ کوئی بھی اسے آپ سے چھین نہیں سکتا۔",
      author: "B.B. King",
    },
  ];

  // Initialize quotes rotation
  useEffect(() => {
    const quoteInterval = setInterval(() => {
      setCurrentQuote((prev) => (prev + 1) % quotes.length);
    }, 8000);
    
    return () => clearInterval(quoteInterval);
  }, []);

  // Initialize floating particles
  useEffect(() => {
    const particlesArray = [];
    for (let i = 0; i < 20; i++) {
      particlesArray.push({
        id: i,
        left: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 15}s`,
        animationDuration: `${15 + Math.random() * 10}s`,
      });
    }
    setParticles(particlesArray);
  }, []);

  // Handle code timer countdown
  useEffect(() => {
    let timer;
    if (codeTimer > 0) {
      timer = setTimeout(() => setCodeTimer(codeTimer - 1), 1000);
    } else if (codeTimer === 0 && forgotPasswordStep === 2) {
      Swal.fire({
        title: "Code Expired",
        text: "The verification code has expired. Please request a new one.",
        icon: "warning",
        confirmButtonText: "OK",
      });
    }
    return () => clearTimeout(timer);
  }, [codeTimer]);

  // Calculate password strength
  useEffect(() => {
    const password = forgotPasswordData.newPassword;
    let strength = "weak";
    
    if (password.length >= 8) {
      const hasUpperCase = /[A-Z]/.test(password);
      const hasLowerCase = /[a-z]/.test(password);
      const hasNumbers = /\d/.test(password);
      const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
      
      const score = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecial].filter(Boolean).length;
      
      if (score === 4) strength = "strong";
      else if (score >= 2) strength = "good";
      else if (score >= 1) strength = "fair";
    }
    
    setPasswordStrength(strength);
  }, [forgotPasswordData.newPassword]);

  // Format timer display
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Navigate to next quote
  const nextQuote = () => {
    setCurrentQuote((prev) => (prev + 1) % quotes.length);
  };

  // Navigate to previous quote
  const prevQuote = () => {
    setCurrentQuote((prev) => (prev - 1 + quotes.length) % quotes.length);
  };

  // ============================================
  // AUTHENTICATION HANDLERS
  // ============================================

  // Handle login form submission
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Validation
    if (!loginData.username.trim() || !loginData.password) {
      Swal.fire({
        title: "Validation Error",
        text: "Please enter both username and password",
        icon: "warning",
        confirmButtonText: "OK",
      });
      setIsLoading(false);
      return;
    }

    try {
      // API call for login
      const response = await axios.post(
        AUTH_API,
        {
          action: "login",
          username: loginData.username,
          password: loginData.password,
          remember_me: loginData.rememberMe,
        }
      );

      console.log("Login Response:", response.data);

      if (response.data.success) {
        const user = response.data.user;

        // 1. Store the COMPLETE RAW login response (EXACTLY as logged)
        localStorage.setItem("fullLoginResponse", JSON.stringify(response.data));

        // 2. Store individual derived data for easy access across the app
        if (user) {
          localStorage.setItem("userId", String(user.id || ""));
          localStorage.setItem("userType", user.role || user.user_type || "user");
          localStorage.setItem("userMobile", user.mobile || user.phone || "");
          localStorage.setItem("userName", user.name || "");
          localStorage.setItem("userEmail", user.email || "");
          localStorage.setItem("userCnic", user.cnic || "");
          localStorage.setItem("userStatus", user.status || "");

          // 3. Store Authentication Tokens
          localStorage.setItem("authToken", response.data.auth_token);
          localStorage.setItem("sessionExpiry", response.data.expires_at);
          localStorage.setItem("sessionToken", response.data.auth_token); // Fix for ChatScreen
        }

        // ... (Your existing SweetAlert and redirect code remains here) ...
        Swal.fire({
          title: "Login Successful!",
          html: `
            <div style="text-align: center;">
              <div style="font-size: 4rem; color: #27ae60; margin-bottom: 1rem;">
                ✓
              </div>
              <h3>Welcome back!</h3>
              <p style="font-size: 1.2rem; color: #2c3e50;">
                ${response.data.user?.name || loginData.username}
              </p>
              <p>You have successfully logged in.</p>
              <p><small>Role: ${response.data.user?.role || 'User'}</small></p>
            </div>
          `,
          icon: "success",
          confirmButtonText: "Continue to Dashboard",
          confirmButtonColor: "#27ae60",
          timer: 2000,
          timerProgressBar: true,
        }).then(() => {
          // Redirect to dashboard
          window.location.href = "/dashboard";
        });
      } else {
        Swal.fire({
          title: "Login Failed",
          text: response.data.message || "Invalid username or password",
          icon: "error",
          confirmButtonText: "Try Again",
          confirmButtonColor: "#e74c3c",
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      Swal.fire({
        title: "Login Error",
        text: error.response?.data?.message || "An error occurred during login. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle forgot password email submission
  const handleForgotPasswordEmail = async (e) => {
    e.preventDefault();
    
    if (!forgotPasswordData.email) {
      Swal.fire({
        title: "Email Required",
        text: "Please enter your registered email address",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotPasswordData.email)) {
      Swal.fire({
        title: "Invalid Email",
        text: "Please enter a valid email address",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    setIsSendingCode(true);

    try {
      // Generate a random 6-digit code
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      setGeneratedCode(code);

      // Send email with code
      const response = await axios.post(
        AUTH_API,
        {
          action: "send_code",
          email: forgotPasswordData.email,
          code: code,
        }
      );

      console.log("Send Code Response:", response.data);

      if (response.data.success) {
        setForgotPasswordStep(2);
        setCodeTimer(600); // 10 minutes timer (600 seconds)
        Swal.fire({
          title: "Code Sent!",
          html: `
            <div style="text-align: center;">
              <div style="font-size: 3rem; color: #27ae60; margin-bottom: 1rem;">
                ✉️
              </div>
              <h3>Verification Code Sent</h3>
              <p>A 6-digit verification code has been sent to:</p>
              <p style="font-weight: bold; color: #3498db; font-size: 1.1rem;">
                ${forgotPasswordData.email}
              </p>
              <p>Please check your email and enter the code below.</p>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 8px; margin: 15px 0;">
                <p style="margin: 5px 0;">Code will expire in: <strong style="color: #e74c3c;">10:00</strong></p>
                <p style="margin: 5px 0; font-size: 0.9rem; color: #666;">
                  For testing: Code is <strong>${response.data.data?.code || code}</strong>
                </p>
              </div>
            </div>
          `,
          icon: "success",
          confirmButtonText: "Enter Code",
          confirmButtonColor: "#3498db",
        });
      } else {
        Swal.fire({
          title: "Failed to Send Code",
          text: response.data.message || "Unable to send verification code. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Send code error:", error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to send verification code. Please check your email address or try again later.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSendingCode(false);
    }
  };

  // Handle verification code submission
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!forgotPasswordData.verificationCode) {
      Swal.fire({
        title: "Code Required",
        text: "Please enter the 6-digit verification code",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    if (forgotPasswordData.verificationCode.length !== 6) {
      Swal.fire({
        title: "Invalid Code",
        text: "Please enter a valid 6-digit code",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    if (codeTimer === 0) {
      Swal.fire({
        title: "Code Expired",
        text: "The verification code has expired. Please request a new one.",
        icon: "warning",
        confirmButtonText: "Request New Code",
      });
      return;
    }

    setIsVerifyingCode(true);

    try {
      const response = await axios.post(
        AUTH_API,
        {
          action: "verify_code",
          email: forgotPasswordData.email,
          code: forgotPasswordData.verificationCode,
        }
      );

      console.log("Verify Code Response:", response.data);

      if (response.data.success) {
        setForgotPasswordStep(3);
        Swal.fire({
          title: "Code Verified!",
          html: `
            <div style="text-align: center;">
              <div style="font-size: 3rem; color: #27ae60; margin-bottom: 1rem;">
                ✓
              </div>
              <h3>Verification Successful</h3>
              <p>Your code has been verified successfully.</p>
              <p style="font-weight: bold; color: #27ae60;">
                You can now reset your password.
              </p>
            </div>
          `,
          icon: "success",
          confirmButtonText: "Reset Password",
          confirmButtonColor: "#27ae60",
          timer: 1500,
          timerProgressBar: true,
        });
      } else {
        Swal.fire({
          title: "Invalid Code",
          text: response.data.message || "The verification code is incorrect. Please try again.",
          icon: "error",
          confirmButtonText: "Try Again",
        });
      }
    } catch (error) {
      console.error("Verify code error:", error);
      Swal.fire({
        title: "Verification Error",
        text: error.response?.data?.message || "An error occurred during verification. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsVerifyingCode(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    const { newPassword, confirmPassword } = forgotPasswordData;
    
    // Validations
    if (!newPassword || !confirmPassword) {
      Swal.fire({
        title: "Password Required",
        text: "Please enter and confirm your new password",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }
    
    if (newPassword.length < 6) {
      Swal.fire({
        title: "Weak Password",
        text: "Password must be at least 6 characters long",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      Swal.fire({
        title: "Passwords Don't Match",
        text: "New password and confirmation password do not match",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    setIsResettingPassword(true);

    try {
      const response = await axios.post(
        AUTH_API,
        {
          action: "reset_password",
          email: forgotPasswordData.email,
          code: forgotPasswordData.verificationCode,
          new_password: newPassword,
        }
      );

      console.log("Reset Password Response:", response.data);

      if (response.data.success) {
        Swal.fire({
          title: "Password Reset Successful!",
          html: `
            <div style="text-align: center;">
              <div style="font-size: 4rem; color: #27ae60; margin-bottom: 1rem;">
                🔒✓
              </div>
              <h3>Password Updated</h3>
              <p>Your password has been reset successfully.</p>
              <p style="font-weight: bold; color: #27ae60;">
                You can now login with your new password.
              </p>
              <p><small>All other sessions have been invalidated for security.</small></p>
            </div>
          `,
          icon: "success",
          confirmButtonText: "Go to Login",
          confirmButtonColor: "#27ae60",
        }).then(() => {
          // Reset forgot password flow
          setForgotPasswordMode(false);
          setForgotPasswordStep(1);
          setForgotPasswordData({
            email: "",
            verificationCode: "",
            newPassword: "",
            confirmPassword: "",
          });
          setCodeTimer(0);
        });
      } else {
        Swal.fire({
          title: "Reset Failed",
          text: response.data.message || "Failed to reset password. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Reset password error:", error);
      Swal.fire({
        title: "Reset Error",
        text: error.response?.data?.message || "An error occurred while resetting password. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Resend verification code
  const handleResendCode = async () => {
    if (codeTimer > 300) { // Can resend after 5 minutes
      Swal.fire({
        title: "Wait Required",
        text: "Please wait before requesting a new code",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    setIsSendingCode(true);
    
    // Generate new code
    const newCode = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedCode(newCode);

    try {
      const response = await axios.post(
        AUTH_API,
        {
          action: "resend_code",
          email: forgotPasswordData.email,
          code: newCode,
        }
      );

      console.log("Resend Code Response:", response.data);

      if (response.data.success) {
        setCodeTimer(600); // Reset to 10 minutes
        Swal.fire({
          title: "New Code Sent!",
          html: `
            <div style="text-align: center;">
              <div style="font-size: 3rem; color: #27ae60; margin-bottom: 1rem;">
                ✉️
              </div>
              <h3>New Verification Code Sent</h3>
              <p>A new 6-digit code has been sent to:</p>
              <p style="font-weight: bold; color: #3498db;">
                ${forgotPasswordData.email}
              </p>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 8px; margin: 15px 0;">
                <p>New code expires in: <strong style="color: #e74c3c;">10:00</strong></p>
                <p style="font-size: 0.9rem; color: #666;">
                  For testing: Code is <strong>${newCode}</strong>
                </p>
              </div>
            </div>
          `,
          icon: "success",
          confirmButtonText: "OK",
        });
      } else {
        Swal.fire({
          title: "Resend Failed",
          text: response.data.message || "Failed to resend code. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Resend code error:", error);
      Swal.fire({
        title: "Resend Error",
        text: error.response?.data?.message || "Failed to resend code. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSendingCode(false);
    }
  };

  // Test API connection
  const testAPIConnection = async () => {
    try {
      const response = await axios.get(AUTH_API);
      console.log("API Connection Test:", response.data);
      return response.data;
    } catch (error) {
      console.error("API Connection Error:", error);
      return null;
    }
  };

  // Toggle language
  const toggleLanguage = () => {
    setLanguage(language === "english" ? "urdu" : "english");
  };

  // Go back to login from forgot password
  const handleBackToLogin = () => {
    setForgotPasswordMode(false);
    setForgotPasswordStep(1);
    setForgotPasswordData({
      email: "",
      verificationCode: "",
      newPassword: "",
      confirmPassword: "",
    });
    setCodeTimer(0);
  };

  // Quick login for testing
  const quickLogin = (role) => {
    const credentials = {
      admin: { username: "admin@mhs.edu", password: "password" },
      teacher: { username: "teacher@mhs.edu", password: "password" },
      student: { username: "student@mhs.edu", password: "password" }
    };
    
    if (credentials[role]) {
      setLoginData({
        ...credentials[role],
        rememberMe: false
      });
      
      // Auto-submit after a short delay
      setTimeout(() => {
        document.querySelector('.lgn-login-submit-btn').click();
      }, 100);
    }
  };

  return (
    <div className="lgn-page-container">
      {/* Background Animation */}
      <div className="lgn-bg-animation">
        <div className="lgn-bg-circle lgn-circle-1"></div>
        <div className="lgn-bg-circle lgn-circle-2"></div>
        <div className="lgn-bg-circle lgn-circle-3"></div>
        
        {/* Floating Particles */}
        <div className="lgn-floating-particles">
          {particles.map((particle) => (
            <div
              key={particle.id}
              className="lgn-particle"
              style={{
                left: particle.left,
                animationDelay: particle.animationDelay,
                animationDuration: particle.animationDuration,
              }}
            />
          ))}
        </div>
      </div>

      <div className="lgn-main-content">
        {/* Left Section - Motivational Quotes */}
        <div className="lgn-left-section">
          <div className="lgn-quotes-container">
            {/* School Logo */}
            <div className="lgn-school-logo-container">
              {/* UPDATED: Wrapper added to perfectly center RGB ring without moving the white circle */}
              <div className="lgn-logo-wrapper">
                <div className="lgn-logo-outer-ring"></div>
                <img 
                  src="/images/mainlogo.png" 
                  alt="MHS Public School Logo" 
                  className="lgn-school-main-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://via.placeholder.com/200/1a2a6c/ffffff?text=MHS";
                  }}
                />
              </div>
              <h1 className="lgn-school-name">Meta Hybrid Solutions</h1>
              <p className="lgn-school-tagline">Excellence in Education Since 2023</p>
            </div>

            {/* Motivational Quotes */}
            <div className="lgn-quotes-slider">
              <button 
                className="lgn-quote-icon lgn-quote-icon-left"
                onClick={prevQuote}
                style={{background: 'none', border: 'none', cursor: 'pointer'}}
              >
                <FaCaretLeft />
              </button>
              
              <div className="lgn-quote-content">
                <div className={`lgn-quote-text ${language}`}>
                  <p className="lgn-quote-main">
                    {language === "english" 
                      ? quotes[currentQuote].english
                      : quotes[currentQuote].urdu}
                  </p>
                  <p className="lgn-quote-author">— {quotes[currentQuote].author}</p>
                </div>
              </div>

              <button 
                className="lgn-quote-icon lgn-quote-icon-right"
                onClick={nextQuote}
                style={{background: 'none', border: 'none', cursor: 'pointer'}}
              >
                <FaCaretRight />
              </button>
            </div>

            {/* Quote Navigation */}
            <div className="lgn-quote-navigation">
              {quotes.map((_, index) => (
                <button
                  key={index}
                  className={`lgn-quote-dot ${index === currentQuote ? "lgn-active" : ""}`}
                  onClick={() => setCurrentQuote(index)}
                  aria-label={`Go to quote ${index + 1}`}
                />
              ))}
            </div>

            {/* Language Toggle */}
            <div className="lgn-language-toggle">
              <button 
                className={`lgn-lang-btn ${language === "english" ? "lgn-active" : ""}`}
                onClick={() => setLanguage("english")}
              >
                <FaGlobe /> English
              </button>
              <button 
                className={`lgn-lang-btn ${language === "urdu" ? "lgn-active" : ""}`}
                onClick={() => setLanguage("urdu")}
              >
                <FaLanguage /> اردو
              </button>
            </div>

            {/* School Features */}
            <div className="lgn-school-features">
              <div className="lgn-feature-item">
                <FaGraduationCap className="lgn-feature-icon" />
                <span>Quality Education</span>
              </div>
              <div className="lgn-feature-item">
                <FaBook className="lgn-feature-icon" />
                <span>Modern Curriculum</span>
              </div>
              <div className="lgn-feature-item">
                <FaUsers className="lgn-feature-icon" />
                <span>Expert Faculty</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - Login Form */}
        <div className="lgn-right-section">
          <div className="lgn-form-container">
            {/* Form Header */}
            <div className="lgn-form-header">
              {forgotPasswordMode ? (
                <div className="lgn-forgot-password-header">
                  <FaKey className="lgn-form-header-icon" />
                  <h2>Reset Your Password</h2>
                  <p className="lgn-form-subtitle">
                    {forgotPasswordStep === 1 && "Enter your email to receive a verification code"}
                    {forgotPasswordStep === 2 && "Enter the 6-digit code sent to your email"}
                    {forgotPasswordStep === 3 && "Create a new password for your account"}
                  </p>
                </div>
              ) : (
                <div className="lgn-login-header">
                  <FaSignInAlt className="lgn-form-header-icon" />
                  <h2>Welcome Back</h2>
                  <p className="lgn-form-subtitle">Sign in to your account to continue</p>
                </div>
              )}
            </div>

            {/* Progress Steps for Forgot Password */}
            {forgotPasswordMode && (
              <div className="lgn-forgot-password-progress">
                <div className={`lgn-progress-step ${forgotPasswordStep >= 1 ? "lgn-active" : ""}`}>
                  <div className="lgn-step-number">1</div>
                  <div className="lgn-step-label">Enter Email</div>
                </div>
                <div className="lgn-progress-line"></div>
                <div className={`lgn-progress-step ${forgotPasswordStep >= 2 ? "lgn-active" : ""}`}>
                  <div className="lgn-step-number">2</div>
                  <div className="lgn-step-label">Verify Code</div>
                </div>
                <div className="lgn-progress-line"></div>
                <div className={`lgn-progress-step ${forgotPasswordStep >= 3 ? "lgn-active" : ""}`}>
                  <div className="lgn-step-number">3</div>
                  <div className="lgn-step-label">New Password</div>
                </div>
              </div>
            )}

            {/* Login Form */}
            {!forgotPasswordMode ? (
              <form className="lgn-login-form" onSubmit={handleLoginSubmit}>
                {/* Username Field */}
                <div className="lgn-form-group">
                  <label className="lgn-form-label" htmlFor="username">
                    <FaUser className="lgn-label-icon" />
                    Username / Email
                  </label>
                  <div className="lgn-input-with-icon">
                    <input
                      id="username"
                      type="text"
                      className="lgn-form-input"
                      value={loginData.username}
                      onChange={(e) =>
                        setLoginData({ ...loginData, username: e.target.value })
                      }
                      placeholder="Enter your username or email"
                      required
                      autoFocus
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="lgn-form-group">
                  <label className="lgn-form-label" htmlFor="password">
                    <FaLock className="lgn-label-icon" />
                    Password
                  </label>
                  <div className="lgn-password-input-container">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      className="lgn-form-input"
                      value={loginData.password}
                      onChange={(e) =>
                        setLoginData({ ...loginData, password: e.target.value })
                      }
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      className="lgn-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="lgn-form-options">
                  <label className="lgn-remember-me">
                    <input
                      type="checkbox"
                      checked={loginData.rememberMe}
                      onChange={(e) =>
                        setLoginData({ ...loginData, rememberMe: e.target.checked })
                      }
                    />
                    <span>Remember me</span>
                  </label>
                  <button
                    type="button"
                    className="lgn-forgot-password-link"
                    onClick={() => setForgotPasswordMode(true)}
                  >
                    Forgot Password?
                  </button>
                </div>

                {/* Submit Button */}
                <button
                  id="login-button"
                  type="submit"
                  className="lgn-login-submit-btn"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <FaSync className="lgn-spinner-icon" />
                      Signing In...
                    </>
                  ) : (
                     <>
                      <FaSignInAlt className="lgn-btn-icon" />
                      Sign In
                    </>
                  )}
                </button>

                {/* Quick Test Buttons */}
                <div className="lgn-quick-test-buttons">
                  <button
                    type="button"
                    className="lgn-test-btn lgn-admin-btn"
                    onClick={() => quickLogin('admin')}
                  >
                    Test as Admin
                  </button>
                  <button
                    type="button"
                    className="lgn-test-btn lgn-teacher-btn"
                    onClick={() => quickLogin('teacher')}
                  >
                    Test as Teacher
                  </button>
                  <button
                    type="button"
                    className="lgn-test-btn lgn-student-btn"
                    onClick={() => quickLogin('student')}
                  >
                    Test as Student
                  </button>
                </div>

                {/* Demo Account Info */}
                <div className="lgn-demo-info">
                  <p className="lgn-demo-title">Demo Accounts (For Testing):</p>
                  <div className="lgn-demo-accounts">
                    <div className="lgn-demo-account">
                      <strong>Admin:</strong> admin@mhs.edu | password: password
                    </div>
                    <div className="lgn-demo-account">
                      <strong>Teacher:</strong> teacher@mhs.edu | password: password
                    </div>
                    <div className="lgn-demo-account">
                      <strong>Student:</strong> student@mhs.edu | password: password
                    </div>
                  </div>
                  <p className="lgn-demo-note">
                    <small>All demo accounts use password: <strong>password</strong></small>
                  </p>
                </div>
              </form>
            ) : (
              /* Forgot Password Forms */
              <div className="lgn-forgot-password-forms">
                {/* Step 1: Enter Email */}
                {forgotPasswordStep === 1 && (
                  <form className="lgn-forgot-form" onSubmit={handleForgotPasswordEmail}>
                    <div className="lgn-form-group">
                      <label className="lgn-form-label" htmlFor="forgotEmail">
                        <FaEnvelope className="lgn-label-icon" />
                        Registered Email Address
                      </label>
                      <div className="lgn-input-with-icon">
                        <input
                          id="forgotEmail"
                          type="email"
                          className="lgn-form-input"
                          value={forgotPasswordData.email}
                          onChange={(e) =>
                            setForgotPasswordData({
                              ...forgotPasswordData,
                              email: e.target.value,
                            })
                          }
                          placeholder="Enter your registered email"
                          required
                          autoFocus
                        />
                      </div>
                      <p className="lgn-form-hint">
                        A verification code will be sent to this email address (valid for 10 minutes).
                      </p>
                    </div>

                    <div className="lgn-form-actions">
                      <button
                        type="button"
                        className="lgn-back-to-login-btn"
                        onClick={handleBackToLogin}
                      >
                        Back to Login
                      </button>
                      <button
                        type="submit"
                        className="lgn-send-code-btn"
                        disabled={isSendingCode}
                      >
                        {isSendingCode ? (
                          <>
                            <FaSync className="lgn-spinner-icon" />
                            Sending Code...
                          </>
                        ) : (
                          <>
                            <FaKey className="lgn-btn-icon" />
                            Send Verification Code
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}

                {/* Step 2: Enter Verification Code */}
                {forgotPasswordStep === 2 && (
                  <form className="lgn-forgot-form" onSubmit={handleVerifyCode}>
                    <div className="lgn-code-input-section">
                      <div className="lgn-timer-display">
                        <FaClock className="lgn-timer-icon" />
                        <span className="lgn-timer-text">
                          Code expires in: <strong>{formatTimer(codeTimer)}</strong>
                        </span>
                      </div>

                      <div className="lgn-form-group">
                        <label className="lgn-form-label" htmlFor="verificationCode">
                          <FaQrcode className="lgn-label-icon" />
                          6-Digit Verification Code
                        </label>
                        <div className="lgn-code-input-container">
                          <input
                            id="verificationCode"
                            type="text"
                            className="lgn-form-input lgn-code-input"
                            value={forgotPasswordData.verificationCode}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                              setForgotPasswordData({
                                ...forgotPasswordData,
                                verificationCode: value,
                              });
                            }}
                            placeholder="000000"
                            maxLength="6"
                            required
                            autoFocus
                          />
                        </div>
                        <p className="lgn-form-hint">
                          Enter the 6-digit code sent to {forgotPasswordData.email}
                        </p>
                      </div>

                      <div className="lgn-resend-code-section">
                        <p>Didn't receive the code?</p>
                        <button
                          type="button"
                          className="lgn-resend-code-btn"
                          onClick={handleResendCode}
                          disabled={isSendingCode || codeTimer > 300}
                        >
                          {isSendingCode ? (
                            <>
                              <FaSync className="lgn-spinner-icon" />
                              Sending...
                            </>
                          ) : (
                            "Resend Code"
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="lgn-form-actions">
                      <button
                        type="button"
                        className="lgn-back-to-email-btn"
                        onClick={() => setForgotPasswordStep(1)}
                      >
                        Change Email
                      </button>
                      <button
                        type="submit"
                        className="lgn-verify-code-btn"
                        disabled={isVerifyingCode}
                      >
                        {isVerifyingCode ? (
                          <>
                            <FaSync className="lgn-spinner-icon" />
                            Verifying...
                          </>
                        ) : (
                          <>
                            <FaCheckCircle className="lgn-btn-icon" />
                            Verify Code
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}

                {/* Step 3: Reset Password */}
                {forgotPasswordStep === 3 && (
                  <form className="lgn-forgot-form" onSubmit={handleResetPassword}>
                    <div className="lgn-form-group">
                      <label className="lgn-form-label" htmlFor="newPassword">
                        <FaLock className="lgn-label-icon" />
                        New Password
                      </label>
                      <div className="lgn-password-input-container">
                        <input
                          id="newPassword"
                          type="password"
                          className="lgn-form-input"
                          value={forgotPasswordData.newPassword}
                          onChange={(e) =>
                            setForgotPasswordData({
                              ...forgotPasswordData,
                              newPassword: e.target.value,
                            })
                          }
                          placeholder="Enter new password (min. 6 characters)"
                          required
                          minLength="6"
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="lgn-form-group">
                      <label className="lgn-form-label" htmlFor="confirmPassword">
                        <FaLock className="lgn-label-icon" />
                        Confirm New Password
                      </label>
                      <div className="lgn-password-input-container">
                        <input
                          id="confirmPassword"
                          type="password"
                          className="lgn-form-input"
                          value={forgotPasswordData.confirmPassword}
                          onChange={(e) =>
                            setForgotPasswordData({
                              ...forgotPasswordData,
                              confirmPassword: e.target.value,
                            })
                          }
                          placeholder="Confirm new password"
                          required
                          minLength="6"
                        />
                      </div>
                    </div>

                    <div className="lgn-password-strength">
                      <div className={`lgn-strength-indicator ${passwordStrength}`}>
                        <div className="lgn-strength-bar"></div>
                        <div className="lgn-strength-text">
                          Password Strength:{" "}
                          <strong style={{
                            color: 
                              passwordStrength === "strong" ? "#27ae60" :
                              passwordStrength === "good" ? "#f39c12" :
                              passwordStrength === "fair" ? "#e67e22" : "#e74c3c"
                          }}>
                            {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                          </strong>
                        </div>
                      </div>
                    </div>

                    <div className="lgn-form-actions">
                      <button
                        type="button"
                        className="lgn-back-to-code-btn"
                        onClick={() => setForgotPasswordStep(2)}
                      >
                        Back to Code
                      </button>
                      <button
                        type="submit"
                        className="lgn-reset-password-btn"
                        disabled={isResettingPassword}
                      >
                        {isResettingPassword ? (
                          <>
                            <FaSync className="lgn-spinner-icon" />
                            Resetting...
                          </>
                        ) : (
                          <>
                            <FaKey className="lgn-btn-icon" />
                            Reset Password
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* Login Footer */}
            <div className="lgn-footer">
              <div className="lgn-security-info">
                <FaShieldAlt className="lgn-security-icon" />
                <span>Your login is secured with 256-bit SSL encryption</span>
              </div>
              <div className="lgn-support-info">
                <p>Need help? <a href="mailto:support@mhsschool.edu.pk">Contact Support</a></p>
              </div>
              <div className="lgn-copyright">
                &copy; {new Date().getFullYear()} MHS Public School. All rights reserved.
                <br />
                Version 2.0.1 | API: users.php
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;